import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectTheme,
  selectThemePreference,
  setThemePreference,
  systemThemeChanged,
  type ThemePreference,
} from '@/store/features/theme.feature';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  AppearanceContext,
  type AppearanceContextValue,
} from './appearance-context';
import { appearanceTokens } from './appearance-colour.util';
import {
  applyAppearance,
  applyAppearanceTokens,
  clearAppearance,
} from './appearance-engine.util';
import {
  DEFAULT_LOOK,
  looksEqual,
  lookOf,
  type AppearanceLook,
  type AppearanceV1,
} from './appearance.util';
import {
  useAppearanceState,
  type AppearanceStateResult,
} from './use-appearance-state.hook';

const QUERY = '(prefers-color-scheme: dark)';

/**
 * Keeps the theme's mode and the saved appearance's mode as one.
 *
 * Each time the saved appearance comes back (at start, and again after the
 * user signed out), the mode on screen is weighed
 * against the one the server had when this browser last heard from it. The
 * same means nothing changed here, so a mode another device saved is put on
 * screen without being saved back. Different means it changed here and the
 * server never took it (a failed save, cmd+shift+L outside the shell, or a
 * switch while the preferences loaded), so it
 * stays and is saved. Later, a mode another device saved is taken while
 * nothing was changed here, and any change of mode, from the account menu,
 * cmd+shift+L or the picker, saves the appearance with it.
 */
function useModeSync(
  state: AppearanceStateResult,
  preference: ThemePreference
): void {
  const dispatch = useAppDispatch();
  // The mode before the saved appearance came back: at mount, or when the
  // sync last paused. It stands in for the server's mode when this browser
  // has no copy to say.
  const before = useRef(preference);
  // The mode the server holds, as far as this tab knows; null while paused.
  const synced = useRef<ThemePreference | null>(null);
  const lastSaved = useRef<AppearanceV1 | null | undefined>(undefined);
  const { ready, saved, syncedMode, changed, appearance, commit } = state;
  const live = ready;

  useEffect(() => {
    if (!live) {
      // Paused: it starts over when the appearance is back, from here.
      if (synced.current !== null) before.current = preference;
      synced.current = null;
      return;
    }
    if (synced.current === null) {
      lastSaved.current = saved;
      const serverMode = saved?.mode ?? null;
      const known = syncedMode ?? before.current;
      if (
        serverMode !== null &&
        serverMode !== preference &&
        preference === known
      ) {
        synced.current = serverMode;
        dispatch(setThemePreference(serverMode));
        return;
      }
      synced.current = serverMode ?? known;
    } else if (saved !== lastSaved.current) {
      lastSaved.current = saved;
      if (!changed && saved && saved.mode !== preference) {
        synced.current = saved.mode;
        dispatch(setThemePreference(saved.mode));
        return;
      }
    }
    if (preference === synced.current) return;
    synced.current = preference;
    commit(lookOf(appearance));
  }, [
    appearance,
    changed,
    commit,
    dispatch,
    live,
    preference,
    saved,
    syncedMode,
  ]);
}

/**
 * The theme engine, mounted once around the shell. It paints the user's
 * appearance on `<html>` before the browser paints (from this browser's copy
 * until the preferences load), follows the system's mode while the user
 * picked System, and saves mode changes with the appearance.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const preference = useAppSelector(selectThemePreference);
  const mode = useAppSelector(selectTheme);
  const state = useAppearanceState({ mode: preference });
  useModeSync(state, preference);

  const look = useMemo(() => lookOf(state.appearance), [state.appearance]);
  const tokens = useMemo(() => appearanceTokens(look), [look]);
  const tokensNow = useRef(tokens);
  tokensNow.current = tokens;

  // Before the browser paints this commit, so the tinted chrome never shows
  // today's colours first.
  useLayoutEffect(() => {
    applyAppearanceTokens(document.documentElement, tokens);
  }, [tokens]);
  useLayoutEffect(() => () => clearAppearance(document.documentElement), []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(QUERY);
    const follow = () =>
      dispatch(systemThemeChanged(query.matches ? 'dark' : 'light'));
    // The system may have switched before the shell mounted.
    follow();
    query.addEventListener('change', follow);
    return () => query.removeEventListener('change', follow);
  }, [dispatch]);

  // One paint per animation frame, of the newest look previewed in it.
  const frame = useRef<number | null>(null);
  const scheduled = useRef(false);
  const pending = useRef<AppearanceLook | null>(null);
  const cancelFrame = useCallback(() => {
    if (scheduled.current && frame.current !== null)
      cancelAnimationFrame(frame.current);
    scheduled.current = false;
    frame.current = null;
    pending.current = null;
  }, []);
  useEffect(() => cancelFrame, [cancelFrame]);

  const preview = useCallback(
    (next: AppearanceLook | null) => {
      if (next === null) {
        cancelFrame();
        applyAppearanceTokens(document.documentElement, tokensNow.current);
        return;
      }
      pending.current = next;
      if (scheduled.current) return;
      scheduled.current = true;
      frame.current = requestAnimationFrame(() => {
        scheduled.current = false;
        frame.current = null;
        const latest = pending.current;
        pending.current = null;
        if (latest) applyAppearance(document.documentElement, latest);
      });
    },
    [cancelFrame]
  );

  const {
    commit: commitState,
    reset: resetState,
    ready,
    failed,
    retry,
  } = state;
  const commit = useCallback(
    (next: AppearanceLook) => {
      cancelFrame();
      // Nothing changed: paint the saved look over whatever was previewed.
      if (!commitState(next))
        applyAppearanceTokens(document.documentElement, tokensNow.current);
    },
    [cancelFrame, commitState]
  );
  const reset = useCallback(() => {
    cancelFrame();
    resetState();
  }, [cancelFrame, resetState]);
  const setPreference = useCallback(
    (next: ThemePreference) => dispatch(setThemePreference(next)),
    [dispatch]
  );

  const value = useMemo<AppearanceContextValue>(
    () => ({
      ready,
      failed,
      retry,
      preference,
      mode,
      look,
      isDefault: looksEqual(look, DEFAULT_LOOK),
      setPreference,
      preview,
      commit,
      reset,
    }),
    [
      commit,
      failed,
      look,
      mode,
      preference,
      preview,
      ready,
      reset,
      retry,
      setPreference,
    ]
  );

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
}
